import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Building2, Box, Users, FileText,
  CreditCard, KeyRound, Settings, LogOut, ChevronDown,
  Map, ClipboardList, ShieldCheck, History, Inbox,
  BarChart3, Gavel, Plug, Layers,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useFacility } from '../../contexts/FacilityContext'
import { useState } from 'react'
import clsx from 'clsx'
import { ConfirmModal } from '../ui'

const navItems = [
  { to: '/dashboard',    label: 'Dashboard',    icon: LayoutDashboard },
  { to: '/facilities',   label: 'Facilities',   icon: Building2 },
  { label: 'Units', icon: Box, children: [
    { to: '/units/map',   label: 'Unit Map',     icon: Map },
    { to: '/units',       label: 'All Units',    icon: Box },
    { to: '/units/types', label: 'Unit Types',   icon: Layers },
  ]},
  { to: '/tenants',      label: 'Tenants',      icon: Users },
  { to: '/rentals',      label: 'Rentals',      icon: ClipboardList },
  { to: '/invoices',     label: 'Invoices',     icon: FileText },
  { to: '/payments',     label: 'Payments',     icon: CreditCard },
  { to: '/access-codes', label: 'Access Codes', icon: KeyRound },
  { to: '/move-out-requests', label: 'Move-Out Requests', icon: Inbox },
  { to: '/reports',      label: 'Reports',      icon: BarChart3 },
  { to: '/auctions',     label: 'Auctions',     icon: Gavel },
  { to: '/integrations/accounting', label: 'Accounting', icon: Plug },
  { to: '/activity',     label: 'Activity',     icon: History },
]

function NavItem({ item }) {
  const [open, setOpen] = useState(false)

  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setOpen(o => !o)}
          className="sidebar-link-inactive w-full text-left"
        >
          <item.icon size={17} />
          <span className="flex-1">{item.label}</span>
          <ChevronDown size={14} className={clsx('transition-transform', open && 'rotate-180')} />
        </button>
        {open && (
          <div className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-3">
            {item.children.map(child => (
              <NavLink
                key={child.to}
                to={child.to}
                // `end` makes the match exact. Without it, react-router
                // v6 also treats descendant paths as a match - so on
                // /units/map BOTH "Unit Map" (/units/map) and
                // "All Units" (/units) would light up because /units
                // is a prefix of /units/map.
                end
                className={({ isActive }) =>
                  isActive ? 'sidebar-link-active text-sm' : 'sidebar-link-inactive text-sm'
                }
              >
                <child.icon size={15} />
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <NavLink
      to={item.to}
      end
      className={({ isActive }) =>
        isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'
      }
    >
      <item.icon size={17} />
      {item.label}
    </NavLink>
  )
}

export default function Sidebar() {
  const { user, logout } = useAuth()
  const { facilities, activeFacility, switchFacility } = useFacility()
  const navigate = useNavigate()
  const [facilityOpen, setFacilityOpen] = useState(false)
  // QA round 7 #9 - confirm before tearing down the session so the
  // operator can't lose context on an accidental click.
  const [confirmLogout, setConfirmLogout] = useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <aside className="w-60 min-h-screen bg-slate-950 flex flex-col flex-shrink-0">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center">
            <Box size={16} className="text-white" />
          </div>
          <span className="font-display font-700 text-white text-lg tracking-tight">Stovalt</span>
        </div>
      </div>

      {/* Facility Switcher */}
      {facilities.length > 0 && (
        <div className="px-3 py-3 border-b border-white/5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 mb-1.5">Facility</p>
          <div className="relative">
            <button
              onClick={() => setFacilityOpen(o => !o)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/8 transition-colors text-left"
            >
              <Building2 size={14} className="text-brand-400 flex-shrink-0" />
              <span className="text-sm text-white font-medium flex-1 truncate">
                {activeFacility?.name ?? 'Select facility'}
              </span>
              <ChevronDown size={13} className={clsx('text-slate-400 transition-transform', facilityOpen && 'rotate-180')} />
            </button>

            {facilityOpen && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-slate-900 rounded-lg border border-white/10 shadow-xl z-50 overflow-hidden">
                {facilities.map(f => (
                  <button
                    key={f.id}
                    onClick={() => { switchFacility(f); setFacilityOpen(false) }}
                    className={clsx(
                      'w-full text-left px-3 py-2.5 text-sm transition-colors flex items-center justify-between gap-2',
                      f.id === activeFacility?.id
                        ? 'bg-brand-500/10 text-brand-400'
                        : 'text-slate-300 hover:bg-white/5'
                    )}
                  >
                    <span className="truncate">{f.name}</span>
                    {f.pivot?.role && f.pivot.role !== 'owner' && (
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 flex-shrink-0">
                        {f.pivot.role}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map((item, i) => <NavItem key={i} item={item} />)}

        {user?.is_platform_admin && (
          <>
            <div className="mt-4 mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Platform
            </div>
            <NavItem item={{ to: '/admin/platform-settings', label: 'Platform Settings', icon: ShieldCheck }} />
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-white/5 space-y-0.5">
        <NavLink
          to="/settings"
          className={({ isActive }) => isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}
        >
          <Settings size={17} />
          Settings
        </NavLink>
        <button onClick={() => setConfirmLogout(true)} className="sidebar-link-inactive w-full">
          <LogOut size={17} />
          Log out
        </button>

        {/* User pill */}
        <div className="mt-3 px-3 py-2.5 rounded-lg bg-white/5 flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-brand-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-brand-400">
              {user?.name?.[0]?.toUpperCase() ?? 'U'}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 capitalize">{user?.subscription_plan} plan</p>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        onConfirm={handleLogout}
        title="Log out"
        message="Are you sure you want to log out? Any unsaved work will be lost."
        confirmLabel="Log out"
      />
    </aside>
  )
}
